import React from 'react';
import { MapPin, Info, ExternalLink, Map as MapIcon } from 'lucide-react';

interface CultivationMapProps {
  lat: number;
  lng: number;
  onLocationChange?: (lat: number, lng: number) => void;
  readOnly?: boolean;
}

export const CultivationMap: React.FC<CultivationMapProps> = ({ 
  lat, 
  lng,
  readOnly = false
}) => {
  const [hasError, setHasError] = React.useState(false);

  // Reset error when coordinates change
  React.useEffect(() => {
    setHasError(false);
  }, [lat, lng]);

  // If no location set, show a placeholder
  if (lat === undefined || lng === undefined || (lat === 0 && lng === 0)) {
    return (
      <div className="w-full h-48 bg-stone-100 rounded-2xl flex flex-col items-center justify-center border border-stone-200 border-dashed gap-3">
        <div className="p-3 bg-stone-200 rounded-full text-stone-400">
          <MapPin size={24} />
        </div>
        <p className="text-xs text-stone-400 font-medium text-center px-6">
          {readOnly ? 'Location coordinates not available for this profile.' : "Location not set. Click 'Get Current Location' or set manually."}
        </p>
      </div>
    );
  }

  // Use the server-side proxy endpoint
  const mapUrl = `/api/maps/static?lat=${lat}&lng=${lng}&zoom=16&size=600x300&scale=2`;
  
  // OpenStreetMap embed URL
  const osmEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.005},${lat-0.005},${lng+0.005},${lat+0.005}&layer=mapnik&marker=${lat},${lng}`;

  // Universal Link for Google Maps (best for triggering the native app)
  const googleMapsAppUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  const handleOpenMaps = (e: React.MouseEvent) => {
    const ua = navigator.userAgent;
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    if (isAndroid) {
      e.preventDefault();
      // geo: URI scheme triggers the native Android Maps app directly
      window.location.href = `geo:${lat},${lng}?q=${lat},${lng}`;
    } else if (isIOS) {
      e.preventDefault();
      // comgooglemaps:// triggers the native iOS Maps app
      window.location.href = `comgooglemaps://?q=${lat},${lng}`;
      
      // Fallback to browser if Google Maps app isn't responding
      setTimeout(() => {
        if (!document.hidden) {
          window.open(googleMapsAppUrl, '_blank');
        }
      }, 1000);
    }
    // Desktop users will follow the default href
  };

  return (
    <div className="space-y-3">
      <div className="w-full h-48 rounded-2xl overflow-hidden border border-stone-200 shadow-sm relative bg-stone-50 group">
        {!hasError ? (
          <img 
            src={mapUrl} 
            alt="Farm Location Map"
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setHasError(true)}
          />
        ) : (
          <iframe 
            width="100%" 
            height="100%" 
            frameBorder="0" 
            scrolling="no" 
            marginHeight={0} 
            marginWidth={0} 
            src={osmEmbedUrl}
            title="Farm Location Fallback"
            className="w-full h-full"
          ></iframe>
        )}
        
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none" />
        
        <div className="absolute bottom-3 right-3 flex flex-col gap-2">
          <a 
            href={googleMapsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#4285F4] text-white px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 text-xs font-bold hover:bg-[#3367D6] transition-all active:scale-95"
            onClick={handleOpenMaps}
          >
            <MapIcon size={14} />
            Open in Google Maps App
          </a>
        </div>
      </div>
      
      <div className="flex items-start gap-3 bg-stone-50 p-3 rounded-xl border border-stone-100">
        <Info size={14} className="text-stone-400 mt-0.5 shrink-0" />
        <p className="text-[10px] text-stone-500 leading-relaxed">
          The map view shows your farm's approximate location based on coordinates: <span className="font-mono text-stone-700">{lat.toFixed(4)}, {lng.toFixed(4)}</span>.
          {hasError && " (Using OpenStreetMap fallback)"}
        </p>
      </div>
    </div>
  );
};
